// <copyright file="LoginPortal.tsx">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import React, { useState, useContext } from "react";
import styles from "src/components/LoginPortal/LoginPortal.module.css";
import ConfigContext from "src/context/ConfigContext";
import type { FC } from "react";
import type { IConfig } from "src/utils/ConfigLoader";

interface LoginPortalProps {}

interface ILoginProvidersInterface {
    [key: string]: {
        clientId: string | undefined;
        name: string;
        enabled: boolean;
        displayName: string;
        scope: string;
    };
}

const LoginPortal: FC<LoginPortalProps> = () => {
    const configData = useContext(ConfigContext) as IConfig;

    const providers: string[] = [];
    const loginProviders: ILoginProvidersInterface = {};
    configData.identityProviders.forEach((element) => {
        if (element.enabled) providers.push(element.displayName);
        loginProviders[element.displayName] = { ...element };
    });

    const [selectedProvider, setSelectedProvider] = useState(providers[0]);

    function onProviderChange(e: { value: React.SetStateAction<string> }) {
        const value = e?.value ?? providers[0];
        setSelectedProvider(value);
    }

    function continueClick() {
        // Cognito client apps must be configured with a matching allowed login callback url
        document.cookie = `idp=${loginProviders[selectedProvider].name}; SameSite=Strict`;
        const uri = `${configData.cognitoAuth.cognitoAuthUrl}/login?client_id=${loginProviders[selectedProvider].clientId}&response_type=token&scope=${loginProviders[selectedProvider].scope}&redirect_uri=${configData.loginRedirectUrl}`;
        window.location.assign(encodeURI(uri));
    }

    return (
        <div className={styles.LoginPortal} data-testid="LoginPortal">
            <div className={styles.LoginPortal_content}>
                <div className={styles.LoginPortal_logos}>
                    {configData.logos?.owner ? (
                        <div>
                            <img
                                src={configData.logos.owner}
                                width="200px"
                                alt="logo-owner"
                            />
                            <img
                                src={configData.logos.application}
                                width="200px"
                                alt="logo-app"
                            />
                        </div>
                    ) : (
                        <img
                            src={configData.logos?.application}
                            width="300px"
                            alt="logo-app"
                        />
                    )}
                </div>
                <label htmlFor="select-login-provider">
                    <p>Please select a login identity provider</p>
                </label>
                <Dropdown
                    id="select-login-provider"
                    title="Select login provider"
                    value={selectedProvider}
                    options={providers}
                    onChange={onProviderChange}
                />
                <Button label="Continue" onClick={continueClick} />
            </div>
        </div>
    );
};

export default LoginPortal;
